// useSkipImageOverlay.js
/**
 * Custom hook to manage skip functionality and overlay display
 * Shows an overlay after 5 consecutive skips asking if the user wants to try a new image
 * Counter resets when loading a new image
 * Overlay only shows once per image
 *
 * @param {Object} params - Hook parameters
 * @param {Function} params.onSkip - Callback function to execute when skipping
 * @param {Function} [params.onGetNewImage] - Optional callback to get a new image
 * @param {Object} params.currentTask - Current task object being reviewed
 * @returns {Object} Hook methods and state
 * @property {Function} handleSkip - Handler for skip action
 * @property {Function} handleClickNewImage - Handler for getting new image
 * @property {Function} resetSkipCounter - Resets consecutive skips counter
 * @property {Object} skipImageOverlay - Overlay control object
 */

import { useState, useCallback, useEffect } from 'react'
import { useOverlay } from '@fs/zion-ui'

export default function useSkipImageOverlay({ onSkip, onGetNewImage, currentTask }) {
  const [consecutiveSkips, setConsecutiveSkips] = useState(0)
  const [hasShownOverlay, setHasShownOverlay] = useState(false)
  const [lastImageUrl, setLastImageUrl] = useState(currentTask?.artifactUrl)
  const skipImageOverlay = useOverlay()

  // Reset counter and overlay shown state if image URL changes
  useEffect(() => {
    if (currentTask?.artifactUrl !== lastImageUrl) {
      setLastImageUrl(currentTask?.artifactUrl)
      setConsecutiveSkips(0)
      setHasShownOverlay(false)
    }
  }, [currentTask?.artifactUrl, lastImageUrl])

  const handleSkip = useCallback(() => {
    const newSkipCount = consecutiveSkips + 1
    setConsecutiveSkips(newSkipCount)

    if (newSkipCount >= 5 && !hasShownOverlay) {
      skipImageOverlay.open()
      setHasShownOverlay(true)
    }

    onSkip()
  }, [consecutiveSkips, hasShownOverlay, onSkip, skipImageOverlay])

  const innerHandleClickNewImage = useCallback(async () => {
    skipImageOverlay.close()
    setConsecutiveSkips(0)
    setHasShownOverlay(false)
    if (typeof onGetNewImage === 'function') {
      try {
        await onGetNewImage()
      } catch (error) {
        console.error('Error occurred while getting a new image:', error)
      }
    }
  }, [skipImageOverlay, onGetNewImage])

  const resetSkipCounter = useCallback(() => {
    setConsecutiveSkips(0)
  }, [])

  return {
    handleSkip,
    handleClickNewImage: innerHandleClickNewImage,
    resetSkipCounter,
    skipImageOverlay,
  }
}
